import React from 'react';
import { capitalize } from '@mui/material';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';
import Select from 'react-select';
import ColorPicker from '../../../Components/ColorPicker';
import { PERIOD_COLUMN_WIDGET_TYPE } from '../../../utils/periodColumnWidget';
import { ASSET_STATUS_WIDGET_TYPE, STATUS_ARROW_WIDGET_TYPE } from '../../../widgets/assetStatusUtils';
import {
	NUMERIC_VALUE_WIDGET_TYPES,
	WATER_TANK_WIDGET_TYPE,
} from '../layout/webDashboardConstants';

const WidgetDynamicPropsSettings = ({
	edit,
	setEdit,
	t,
	alignments,
	selectedDatastream,
	isStringDatastream,
	isNumericDatastream,
	isHiddenWidgetEditorPropKey,
	getEffectiveMinMax,
	normalizeSliderValueInputChange,
	clampToMinMax,
	snapValueToStep,
	updateWidgetLivePropsById,
	themeColorKey,
	themeColorValue,
}) => (
(edit.dataId > 0 || edit.type === WATER_TANK_WIDGET_TYPE)
	&& !['chart', 'table', 'fan', 'text-title', ASSET_STATUS_WIDGET_TYPE, STATUS_ARROW_WIDGET_TYPE].includes(edit.type)
	&& edit.type !== PERIOD_COLUMN_WIDGET_TYPE ?
									Object.keys(edit.props)
										.filter((key) => {
											if (isHiddenWidgetEditorPropKey(key)) {
												return false;
											}
											if (key === 'factor' && (isStringDatastream || (selectedDatastream && !isNumericDatastream))) {
												return false;
											}
											if (key === 'wellBackgroundColor' || key === 'wellBorderColor') {
												return false;
											}
											if (key === 'min' || key === 'max' || key === 'unit') {
												return false;
											}
											if ([
												'options', 'precision', 'startColor', 'stopColor',
										'changeColorBasedOnValue', 'colors', 'colorGrading', 'direction', 'color', 'inactiveColor',
										'activeOptionColor', 'activeBackgroundColor', 'activeTextColor',
										'inactiveBackgroundColor', 'inactiveTextColor',
										'labelShadowEnabled', 'labelShadowBlur', 'labelShadowStrength', 'labelShadowUseValueColor', 'labelShadowColor',
										'labelShadowChangeColorBasedOnValue', 'labelShadowColorGrading', 'labelShadowColors',
										'showUnit', 'showValueLine', 'fontSize', 'labelFontSize', 'valueFontSize',
										'optionWidth', 'optionHeight', 'paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom',
										'dayLabelFontSize', 'contentAlignment', 'titlePosition', 'showTitle', 'statusText'
									].includes(key)) {
										return false;
									}

											if (
												(edit.type === 'slider' || edit.type === 'number') &&
												['buttonBgColor', 'buttonHoverBgColor', 'valueColor', 'valueHoverColor'].includes(key)
											) {
												return false;
											}

											if (edit.type === 'push-button' && ['textColor', 'inactiveTextColor'].includes(key)) {
												return false;
											}

											if (
												edit.type === 'pipe' &&
												[
													'value',
													'direction',
													'flowDirection',
													'color',
													'pipeColor',
													'pipeBackgroundColor',
													'pipeGlassColor',
													'frameBg',
													'frameBorderColor',
													'frameBorderWidth',
													'frameRadius',
													'framePadding',
													'pipeHeight',
													'pipeWidthPercent',
													'pipeBorderColor',
													'clampColor',
													'titleFontSize',
													'statusFontSize',
													'clickToToggle',
													'flowThreshold',
												].includes(key)
											) {
												return false;
											}

											if (
												edit.type === 'elbow' &&
												[
													'value',
													'direction',
													'color',
													'pipeColor',
													'pipeBackgroundColor',
													'pipeGlassColor',
													'pipeBorderColor',
													'clampColor',
													'pipeHeight',
												].includes(key)
											) {
												return false;
											}

											if (
												edit.type === 'water-pump' &&
												[
													'value',
													'direction',
													'color',
													'pipeColor',
													'pipeBackgroundColor',
													'pipeGlassColor',
													'pipeBorderColor',
													'clampColor',
													'pipeHeight',
													'activeBackgroundColor',
													'inactiveBackgroundColor',
													'inactiveColor',
													'offColor',
												].includes(key)
											) {
												return false;
											}

											return true;
										})
										.map((key, idx) => (
											<div className='form-item' key={idx}>
												<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
													{key === 'interactionMode'
														? (t.settingsLabels?.interactionMode || 'Interaction Mode')
														: (t.settingsLabels?.[key] || key)}
												</label>
												{typeof (edit.props[key]) === 'boolean' ?
													(edit.props[key] ?
														<ToggleOn style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit({ ...edit, props: { ...edit.props, [key]: false } })} /> :
														<ToggleOff style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit({ ...edit, props: { ...edit.props, [key]: true } })} />) :
													key === 'interactionMode' ? (
														<Select
															classNamePrefix="react-select"
															options={[
																{ label: t.pushButtonMode || 'Push Button', value: 'push' },
																{ label: t.switchMode || 'Switch', value: 'switch' }
															]}
															value={[
																{ label: t.pushButtonMode || 'Push Button', value: 'push' },
																{ label: t.switchMode || 'Switch', value: 'switch' }
															].find(o => o.value === edit.props[key]) || { label: t.pushButtonMode || 'Push Button', value: 'push' }}
															onChange={selectedOption => setEdit({
																...edit,
																props: {
																	...edit.props,
																	[key]: selectedOption ? selectedOption.value : 'push'
																}
															})}
															isClearable={false}
														/>
													) : key === 'alignment' ? (
														<Select
															classNamePrefix="react-select"
															options={alignments.map(o => ({ label: o.label, value: o.value }))}
															value={alignments.map(o => ({ label: o.label, value: o.value })).find(o => o.value === edit.props[key])}
															onChange={selectedOption => setEdit({
																...edit,
																props: {
																	...edit.props,
																	[key]: selectedOption ? selectedOption.value : ''
																}
															})}
															isClearable={true}
														/>
													) : (key.toLowerCase().includes('color') && key !== 'changeColorBasedOnValue' && key !== 'colors') ?
														<ColorPicker
															id={key}
															color={themeColorValue(edit.props, key)}
															setColor={(c) => setEdit(prev => ({
																...prev,
																props: { ...prev.props, [themeColorKey(key)]: c }
															}))}
														/> :
													((key === 'value' || key === 'defaultValue') && (
														NUMERIC_VALUE_WIDGET_TYPES.has(edit.type) ||
														isNumericDatastream ||
														(!selectedDatastream && edit.type !== 'text')
													)) || key === 'factor' ? (
																	(() => {
																		const { min, max } = getEffectiveMinMax(selectedDatastream, edit.props);
																		const sliderInputStep = edit.type === 'slider'
																			? Math.max(0.000001, Number(edit.props?.step) || 1)
																			: key === 'factor'
																				? 'any'
																				: 'any';
																		return (
																			<input
																			type='number'
																			className='add-column'
																			placeholder={capitalize(key)}
																			min={key === 'factor' ? undefined : min ?? undefined}
																			max={key === 'factor' ? undefined : max ?? undefined}
																			step={sliderInputStep}
																			style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
																			value={edit.props[key] !== null && edit.props[key] !== undefined ? edit.props[key] : ''}
																			onChange={(e) => {
																				const next = e.target.value;
																				if (next === '') {
																					setEdit((prev) => ({
																						...prev,
																						props: { ...prev.props, [key]: '' }
																					}));
																					if (edit.type === 'slider') {
																						updateWidgetLivePropsById(edit.id, { [key]: '' });
																					}
																					return;
																				}
																				const normalizedNextValue = key === 'factor'
																					? Number(next)
																					: edit.type === 'slider'
																					? normalizeSliderValueInputChange(next, edit.props[key], {
																						stepValue: edit.props?.step,
																						ds: selectedDatastream,
																						fallbackProps: edit.props,
																						nativeInputType: e.nativeEvent?.inputType,
																						nativeInputData: e.nativeEvent?.data,
																					})
																					: clampToMinMax(next, selectedDatastream, edit.props);
																				setEdit((prev) => ({
																					...prev,
																					props: { ...prev.props, [key]: normalizedNextValue }
																				}));
																				if (edit.type === 'slider') {
																					updateWidgetLivePropsById(edit.id, { [key]: normalizedNextValue });
																				}
																			}}
																				onBlur={() => {
																				let normalizedValue = key === 'factor'
																					? Number(edit.props[key])
																					: clampToMinMax(edit.props[key], selectedDatastream, edit.props);
																				if (key === 'factor' && !Number.isFinite(normalizedValue)) {
																					normalizedValue = 1;
																				}
																				if (edit.type === 'slider' && normalizedValue !== '' && normalizedValue !== null && normalizedValue !== undefined) {
																					normalizedValue = snapValueToStep(
																						normalizedValue,
																						edit.props?.step,
																						min ?? 0
																					);
																					normalizedValue = clampToMinMax(normalizedValue, selectedDatastream, edit.props);
																				}
																				if (normalizedValue !== edit.props[key]) {
																					setEdit(prev => ({ ...prev, props: { ...prev.props, [key]: normalizedValue } }));
																				}
																				if (edit.type === 'slider') {
																					updateWidgetLivePropsById(edit.id, { [key]: normalizedValue });
																				}
																			}}
																		/>
																		);
																	})()
																) : (
																	<input type='text' className='add-column' placeholder={capitalize(key)}
																		style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
																		value={edit.props[key] !== null ? edit.props[key] : ''}
																		onChange={(e) => setEdit({
																			...edit,
																			props: {
																				...edit.props,
																				[key]: e.target.value
																			}
																		})} />)
																}
											</div>
										)) : null
);

export default WidgetDynamicPropsSettings;
